import {html, nothing} from 'lit';
import type {Nullable} from '#lit/types/general.js';
import type {Cursor} from './cursor.js';

export type BoundingBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type RectSide = 'top' | 'right' | 'bottom' | 'left';
export type Vec2 = {x: number; y: number};
export type Box = {
  x: number;
  y: number;
  w: number;
  h: number;
};

/** Calculate the distance of two points. */
export function distanceOf(p1: Vec2, p2: Vec2): number {
  return Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2));
}

/** Calculate the control point. */
function controlPointOf(
  target: Vec2,
  another: Vec2,
  sideOfTarget: RectSide,
  /**
   * The distance a control point must be far away from the target in the
   * direction of leaving the target.
   */
  minDistanceToTarget = 50
) {
  switch (sideOfTarget) {
    case 'top': {
      return {
        x: target.x,
        y: Math.min((target.y + another.y) / 2, target.y - minDistanceToTarget),
      };
    }
    case 'bottom': {
      return {
        x: target.x,
        y: Math.max((target.y + another.y) / 2, target.y + minDistanceToTarget),
      };
    }
    case 'left': {
      return {
        x: Math.min((target.x + another.x) / 2, target.x - minDistanceToTarget),
        y: target.y,
      };
    }
    case 'right': {
      return {
        x: Math.max((target.x + another.x) / 2, target.x + minDistanceToTarget),
        y: target.y,
      };
    }
  }
}

/** Return the entering angle of a rectangle side. */
function angleOf(enteringSide: RectSide): number {
  switch (enteringSide) {
    case 'left':
      return 0;
    case 'top':
      return 90;
    case 'right':
      return 180;
    case 'bottom':
      return 270;
  }
}

export type ArrowDescriptor = [
  /** start point */
  /* sx: */ number,
  /* sy: */ number,
  /** control point for start point */
  /* c1x: */ number,
  /* c1y: */ number,
  /** control point for end point */
  /* c2x: */ number,
  /* c2y: */ number,
  /** end point */
  /* ex: */ number,
  /* ey: */ number,
  /** angle of end point */
  /* ae: */ number,
  /** angle of start point */
  /* as: */ number,
];

export type ArrowOptions = Partial<{
  padStart: number;
  padEnd: number;
  controlPointStretch: number;
  small?: boolean;
}>;

/**
 * Get parameters to draw an S-curved line between two boxes.
 */
export function getBoxToBoxArrow(
  x0: number,
  y0: number,
  w0: number,
  h0: number,
  x1: number,
  y1: number,
  w1: number,
  h1: number,
  userOptions?: ArrowOptions
): ArrowDescriptor {
  const options = {
    padStart: 0,
    padEnd: 0,
    controlPointStretch: 50,
    ...userOptions,
  };

  /** Points of start box. */
  const startAtBottom = {
    x: x0 + w0 / 2,
    y: y0 + h0 + 2 * options.padStart,
  };

  /** Points of end box. */
  const _endBox = {x: x1, y: y1, w: w1, h: h1};
  const endAtTop = {x: x1 + w1 / 2, y: y1 - 2 * options.padEnd};
  const bestStartPoint = startAtBottom;
  const bestEndPoint = endAtTop;
  const bestStartSide: RectSide = 'bottom';
  const bestEndSide: RectSide = 'top';

  const controlPointForStartPoint = controlPointOf(
    bestStartPoint,
    bestEndPoint,
    bestStartSide,
    options.controlPointStretch
  );

  const controlPointForEndPoint = controlPointOf(
    bestEndPoint,
    bestStartPoint,
    bestEndSide,
    options.controlPointStretch
  );

  return [
    bestStartPoint.x,
    bestStartPoint.y,
    controlPointForStartPoint.x,
    controlPointForStartPoint.y,
    controlPointForEndPoint.x,
    controlPointForEndPoint.y,
    bestEndPoint.x,
    bestEndPoint.y,
    angleOf(bestEndSide),
    angleOf(bestStartSide),
  ];
}

/**
 * Get parameters to draw an S-curved line between two points.
 */
export function getArrow(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  userOptions?: ArrowOptions
): ArrowDescriptor {
  return getBoxToBoxArrow(x0, y0, 0, 0, x1, y1, 0, 0, userOptions);
}

export enum BezierStyle {
  SOLID = 'solid',
  DASHED = 'dashed',
}

/**
 * Class used for drawing Bezier-stye S-curves.
 */
export class Bezier {
  static DEFAULT_SIZE = 2;
  static DEFAULT_COLOR = 'var(--fly-color-body-alt)';

  color = Bezier.DEFAULT_COLOR;
  size = Bezier.DEFAULT_SIZE;
  style = BezierStyle.SOLID;
  cursor?: Cursor;

  sourcePosition?: BoundingBox;
  targetPosition?: BoundingBox;

  protected constructor(params: {
    color?: string;
    size?: number;
    sourcePosition?: BoundingBox;
    targetPosition?: BoundingBox;
  }) {
    this.color = params.color ?? this.color;
    this.size = params.size ?? this.size;
    this.sourcePosition = params.sourcePosition;
    this.targetPosition = params.targetPosition;
  }

  static create() {
    return new Bezier({});
  }

  static from(source: BoundingBox, target: BoundingBox) {
    return new Bezier({sourcePosition: source, targetPosition: target});
  }

  static withCursor(source: BoundingBox, cursor: Cursor) {
    const bezier = new Bezier({sourcePosition: source});
    bezier.cursor = cursor;

    return bezier;
  }

  private render(
    source: BoundingBox,
    target: BoundingBox,
    opts?: ArrowOptions
  ) {
    const [sx, sy, c1x, c1y, c2x, c2y, ex, ey, ae] = getBoxToBoxArrow(
      source.x,
      source.y,
      source.width,
      source.height,
      target.x,
      target.y,
      target.width,
      target.height,
      {padEnd: 0, ...opts}
    );

    return html`
      <svg
        class="bezier-curve"
        width="100%"
        height="100%"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d=${`M ${sx} ${sy} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${ex} ${ey}`}
          stroke=${this.color}
          stroke-width=${this.size}
          fill="none"
          stroke-dasharray=${this.style === BezierStyle.DASHED
            ? '4 3'
            : nothing}
        />
        <polygon
          points=${`0, ${-this.size} ${this.size * 2}, 0, 0, ${this.size}`}
          transform=${`translate(${ex}, ${ey}) rotate(${ae})`}
          fill=${this.color}
        />
      </svg>
    `;
  }

  attachCursor(cursor: Cursor) {
    this.cursor = cursor;
  }

  drawWithCursor(
    start: BoundingBox,
    endPosition: {
      x: number;
      y: number;
    },
    opts: {
      style?: BezierStyle;
      yConstraint?: Nullable<number>;
      xConstraint?: Nullable<number>;
    }
  ) {
    if (!this.cursor) return html``;

    this.sourcePosition = start;
    if (opts.style) this.style = opts.style;

    const x = opts.xConstraint ?? endPosition.x;
    const y = opts.yConstraint ?? endPosition.y;

    return this.render(start, {
      x,
      y,
      width: 0,
      height: 0,
    });
  }

  draw(
    start: BoundingBox,
    end: BoundingBox,
    opts: {
      style?: BezierStyle;
      color?: string;
      arrow?: ArrowOptions;
    } = {}
  ) {
    this.sourcePosition = start;
    this.targetPosition = end;
    if (opts.style) this.style = opts.style;
    if (opts.color) this.color = opts.color;

    return this.render(this.sourcePosition, this.targetPosition, opts.arrow);
  }
}
